/**
 * The Colyseus connection.
 *
 * One rule governs everything below: THIS FILE SENDS ONE MESSAGE TYPE.
 * `input`, plus `ready` and `ping` for handshake and RTT. There is no `move`,
 * no `died`, no `collect`, no `trigger_activated` — every one of those was a
 * client claim in v1 and every one of them was worth money.
 *
 * If you are adding a `room.send(...)` here and its payload contains a noun
 * rather than a button, stop.
 */

import { Client } from 'colyseus.js';
import { ROOM_NAME, SERVER_URL, readMatchParams } from '../config.js';
import { awaitShellSession } from './shellSession.js';

export class Connection {
  /**
   * Does this room bring you back after death?
   *
   * Declared on the base so the death flow is a CONTRACT rather than a
   * `willRespawn?.()` duck-type at the call site. An arena match is somebody's
   * match ending, so the answer is no and the full DeathCam is right; a gym or
   * a scrim overrides it. Selecting that branch on whether a method happens to
   * exist means a rename, or a third connection type, silently shows the wrong
   * screen with no error.
   */
  willRespawn() {
    return false;
  }

  constructor() {
    /** Built in join(), once the shell has told us where the realtime server is. */
    this.client = null;
    this.room = null;
    this._handlers = new Map();
    /** Server tick at the moment of the newest snapshot, and when it arrived. */
    this.rttMs = 0;
    this._pingTimer = null;
  }

  on(event, fn) {
    this._handlers.set(event, fn);
    return this;
  }

  _emit(event, payload) {
    const fn = this._handlers.get(event);
    if (fn) fn(payload);
  }

  /**
   * Joins the match room.
   *
   * The token is MANDATORY and arrives by postMessage from the shell, never in
   * the URL — see `net/shellSession.js`. `ArenaRoom.onAuth` throws 401 without
   * one and there is no guest path into a wagered match, so failing loudly here
   * beats a confusing disconnect three seconds later.
   *
   * `joinById` when the shell named a room, `joinOrCreate` only as a fallback:
   * `joinOrCreate("arena", …)` matches ANY arena room with a free seat, so with
   * two matches live it can drop you into the wrong one. `onAuth` refuses that
   * (no captured entry for this match) — safe, but it reads to the player as a
   * random failure to join.
   */
  async join() {
    const shell = await awaitShellSession();
    if (!shell.sessionToken) {
      throw new Error('no session token — open the game from the lobby');
    }

    const url = readMatchParams();
    const matchId = shell.matchId || url.matchId;
    this.client = new Client(shell.realtimeUrl || SERVER_URL);

    const options = { token: shell.sessionToken, matchId, lobbyId: url.lobbyId };
    this.room = shell.roomId
      ? await this.client.joinById(shell.roomId, options)
      : await this.client.joinOrCreate(ROOM_NAME, options);
    this._wire();
    return this.room;
  }

  _wire() {
    const room = this.room;

    // Everything the client needs to build the world and predict its own body.
    // Notably absent: the seed. It is revealed at settle, because handing it out
    // now would let a client derive the chaos schedule.
    room.onMessage('world_init', (msg) => this._emit('world_init', msg));

    // Per-client, visibility-filtered. An invisible opponent is ABSENT from
    // these bytes rather than flagged in them.
    room.onMessage('snapshot', (msg) => this._emit('snapshot', msg));

    room.onMessage('events', (events) => this._emit('events', events));

    room.onMessage('result', (msg) => this._emit('result', msg));

    // The room's answer to a client that sent something it no longer accepts.
    room.onMessage('rejected', (msg) => console.warn('[net] rejected:', msg));

    room.onMessage('pong', (msg) => {
      this.rttMs = Date.now() - msg.clientTime;
      this._emit('pong', { ...msg, rttMs: this.rttMs });
    });

    room.onStateChange((state) => this._emit('state', state));
    room.onLeave((code) => {
      this._stopPing();
      this._emit('leave', code);
    });
    room.onError((code, message) => this._emit('error', { code, message }));

    this._startPing();
  }

  /** Tells the room this client has finished loading. Starts the drop early. */
  sendReady() {
    this.room?.send('ready');
  }

  /**
   * Ships a batch of input frames.
   *
   * Batching is not an optimisation detail — it is what keeps a 30Hz input rate
   * under the room's `MAX_INPUTS_PER_SEC` budget without one packet per tick.
   */
  sendInput(frames) {
    if (!this.room || frames.length === 0) return;
    this.room.send('input', { frames });
  }

  _startPing() {
    this._stopPing();
    // The room rate-limits pings to one per 250ms; 1Hz is plenty for an RTT
    // readout and leaves the budget alone.
    this._pingTimer = setInterval(() => {
      this.room?.send('ping', { clientTime: Date.now() });
    }, 1000);
  }

  _stopPing() {
    if (this._pingTimer) clearInterval(this._pingTimer);
    this._pingTimer = null;
  }

  leave() {
    this._stopPing();
    void this.room?.leave();
    this.room = null;
  }
}
